import { useState } from "react";
import { useTranslation } from "react-i18next";

import { TransactionRow } from "@/entities/transaction";
import { useTransactions, type TransactionType } from "@/entities/wallet";
import { staggerIndex } from "@/shared/lib";
import { Button } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * История операций. Figma: `Wallet · Transaction History` (24:240).
 *
 * Курсорная подгрузка: страница берётся по `next_cursor`, конец определяется
 * по `has_more`. Кнопка вместо автоподгрузки по скроллу — при лимите 60
 * запросов в минуту дребезг наблюдателя пересечения обходится дорого.
 */
const FILTERS: readonly TransactionType[] = [
  "deposit",
  "withdrawal",
  "robot_purchase",
  // Пополнение робота — операция, которую делает сам пользователь, и по ней
  // логично искать. `platform_fee` и `referral_bonus` в списке нет намеренно:
  // их никто не инициирует, и фильтр по ним никому не нужен.
  "robot_topup",
  "daily_profit",
  "daily_loss",
];

export function TransactionsPage() {
  const { t } = useTranslation();
  const [type, setType] = useState<TransactionType | undefined>(undefined);
  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTransactions(type);

  const items = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("wallet.history")} subtitle={t("wallet.historySubtitle")} />

      <div
        role="group"
        aria-label={t("wallet.filterType")}
        className="no-scrollbar flex gap-1 overflow-x-auto pb-1"
      >
        {FILTERS.map((option) => {
          const active = type === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={active}
              data-haptic="select"
              onClick={() => {
                // Повторный клик снимает фильтр — кнопки «Все» в макете нет.
                setType(active ? undefined : option);
              }}
              className={[
                "press shrink-0 rounded-[10px] border border-border-subtle p-2.5 text-caption font-semibold",
                "transition-colors duration-[var(--motion-fast)] ease-standard",
                active ? "bg-surface-selected text-text-primary" : "text-text-tertiary",
              ].join(" ")}
            >
              {t(`wallet.txType.${option}`)}
            </button>
          );
        })}
      </div>

      {isPending ? (
        <ScreenSkeleton blocks={[92, 92, 92]} />
      ) : isError ? (
        <ScreenState
          tone="loss"
          icon="warningFill"
          title={t("states.error.title")}
          body={t("states.error.body")}
          action={{ label: t("states.error.action"), onClick: () => void refetch() }}
        />
      ) : items.length === 0 ? (
        <ScreenState
          tone="info"
          icon="trayFill"
          title={t("wallet.emptyHistory.title")}
          body={t("wallet.emptyHistory.body")}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {items.map((item, index) => (
              <li key={item.id} className="stagger" style={staggerIndex(index)}>
                <TransactionRow item={item} />
              </li>
            ))}
          </ul>

          {hasNextPage ? (
            <Button
              size="cta"
              variant="ghost"
              block
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {t("common.loadMore")}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
